import {faDownload} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {ProjectApi} from "../../../api/authority/generated/index.ts";
import {useErrorTranslation} from "../../../api/utilities/translateErrorMessage.js";
import Spinner from "../../../components/Spinner.jsx";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {SortableHeader, TablePagination} from "../../admin/ReportingLab.jsx";

export const MAX_INVOICES = 100;
export const INVOICE_CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_INVOICE_REQUESTS = 4;
const MAX_CACHED_PROJECTS = 50;
const invoiceHistoryCache = new Map();
const invoiceHistoryPromises = new Map();

export function formatInvoiceDate(value, locale = "cs-CZ") {
    const date = value instanceof Date ? value : new Date(value);
    if (!value || Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat(locale, {
        timeZone: "Europe/Prague", day: "numeric", month: "numeric", year: "numeric",
    }).format(date);
}

export function formatInvoiceAmount(amount, currency, locale = "cs-CZ") {
    if (typeof amount !== "number" || !currency) return "—";

    return new Intl.NumberFormat(locale, {
        style: "currency", currency: currency.toUpperCase(),
    }).format(amount / 100);
}

function invoiceTimestamp(invoice) {
    const date = invoice.createdAt instanceof Date ? invoice.createdAt : new Date(invoice.createdAt);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

export function sortInvoicesByCreatedAt(invoices, direction = "desc") {
    return [...invoices].sort((left, right) => {
        const difference = invoiceTimestamp(left) - invoiceTimestamp(right);
        if (difference !== 0) return direction === "asc" ? difference : -difference;
        return String(left.id ?? "").localeCompare(String(right.id ?? ""));
    });
}

export function paginateInvoices(invoices, page, pageSize) {
    const pageCount = Math.max(1, Math.ceil(invoices.length / pageSize));
    const safePage = Math.min(Math.max(page, 1), pageCount);
    return {
        page: safePage,
        rows: invoices.slice((safePage - 1) * pageSize, safePage * pageSize),
    };
}

export function nextInvoiceSortDirection(direction) {
    return direction === "desc" ? "asc" : "desc";
}

export async function loadAllProjectInvoices(api, projectId, isCurrent = () => true) {
    const invoicesById = new Map();
    const visitedCursors = new Set();
    let startingAfter = null;
    let requestCount = 0;
    let truncated = false;

    do {
        requestCount += 1;
        const response = await api.listProjectInvoices({
            projectId,
            ...(startingAfter ? {startingAfter} : {}),
        });
        if (!isCurrent()) return null;

        for (const invoice of response.data ?? []) {
            const identity = invoice.id ?? `${invoice.number ?? ""}:${invoiceTimestamp(invoice)}`;
            if (invoicesById.has(identity)) continue;
            if (invoicesById.size >= MAX_INVOICES) {
                truncated = true;
                continue;
            }
            invoicesById.set(identity, invoice);
        }

        if (response.hasMore !== true) break;

        const nextCursor = response.nextCursor ?? null;
        if (!nextCursor || visitedCursors.has(nextCursor)) {
            throw new Error("Invalid Stripe invoice pagination cursor");
        }
        visitedCursors.add(nextCursor);

        if (invoicesById.size >= MAX_INVOICES || requestCount >= MAX_INVOICE_REQUESTS) {
            truncated = true;
            break;
        }
        startingAfter = nextCursor;
    } while (isCurrent());

    return isCurrent() ? {invoices: [...invoicesById.values()].slice(0, MAX_INVOICES), truncated} : null;
}

export function clearInvoiceHistoryCache(projectId = null) {
    if (projectId === null) {
        invoiceHistoryCache.clear();
        invoiceHistoryPromises.clear();
        return;
    }
    invoiceHistoryCache.delete(projectId);
    invoiceHistoryPromises.delete(projectId);
}

export function loadCachedProjectInvoices(api, projectId, now = Date.now()) {
    const cached = invoiceHistoryCache.get(projectId);
    if (cached && now - cached.loadedAt < INVOICE_CACHE_TTL_MS) {
        return Promise.resolve(cached.response);
    }
    invoiceHistoryCache.delete(projectId);

    const pending = invoiceHistoryPromises.get(projectId);
    if (pending) return pending;

    const promise = loadAllProjectInvoices(api, projectId)
        .then((response) => {
            if (response === null) return response;
            if (!invoiceHistoryCache.has(projectId) && invoiceHistoryCache.size >= MAX_CACHED_PROJECTS) {
                invoiceHistoryCache.delete(invoiceHistoryCache.keys().next().value);
            }
            invoiceHistoryCache.set(projectId, {loadedAt: Date.now(), response});
            return response;
        })
        .finally(() => invoiceHistoryPromises.delete(projectId));

    invoiceHistoryPromises.set(projectId, promise);
    return promise;
}

export default function InvoiceList({project}) {
    const {t, i18n} = useTranslation();
    const {et} = useErrorTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const requestGenerationRef = useRef(0);
    const errorTranslationRef = useRef(et);
    errorTranslationRef.current = et;
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [truncated, setTruncated] = useState(false);
    const [sortDirection, setSortDirection] = useState("desc");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const statusPresentations = {
        draft: {label: t("billing.invoices.status.draft"), className: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"},
        open: {label: t("billing.invoices.status.open"), className: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"},
        paid: {label: t("billing.invoices.status.paid"), className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"},
        uncollectible: {label: t("billing.invoices.status.uncollectible"), className: "bg-red-50 text-datixo-negative dark:bg-red-500/10 text-datixo-negative"},
        void: {label: t("billing.invoices.status.void"), className: "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"},
    };

    useEffect(() => {
        const generation = ++requestGenerationRef.current;
        let active = true;
        const isCurrent = () => active && generation === requestGenerationRef.current;

        setInvoices([]);
        setError(null);
        setTruncated(false);
        setLoading(true);
        setSortDirection("desc");
        setPage(1);

        const api = new ProjectApi(getAuthorityConfiguration());
        loadCachedProjectInvoices(api, project.id)
            .then((response) => {
                if (!isCurrent() || response === null) return;
                setInvoices(response.invoices);
                setTruncated(response.truncated);
            })
            .catch(async (requestError) => {
                const message = await errorTranslationRef.current(requestError);
                if (!isCurrent()) return;
                setError(message || t("billing.invoices.error"));
            })
            .finally(() => {
                if (isCurrent()) setLoading(false);
            });

        return () => {
            active = false;
            requestGenerationRef.current += 1;
        };
    }, [project.id, getAuthorityConfiguration, t]);

    const sortedInvoices = useMemo(() => sortInvoicesByCreatedAt(invoices, sortDirection), [invoices, sortDirection]);
    const {page: safePage, rows: visibleInvoices} = paginateInvoices(sortedInvoices, page, pageSize);
    const sort = {key: "createdAt", direction: sortDirection};
    const toggleCreatedSort = () => {
        setSortDirection(nextInvoiceSortDirection);
        setPage(1);
    };

    if (loading) return <div className="flex min-h-28 items-center justify-center"><Spinner/></div>;

    return <div>
        {error && <div className="app-surface rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-datixo-negative dark:border-red-900/60 dark:bg-red-950/30 text-datixo-negative" role="alert">{error}</div>}
        {!error && invoices.length === 0 && <div className="app-surface rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-400">
            {t("billing.invoices.empty")}
        </div>}
        {!error && invoices.length > 0 && <>
            {truncated && <div className="app-surface mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300" role="status">
                {t("billing.invoices.truncated", {count: MAX_INVOICES})}
            </div>}
            <div className="overflow-x-auto">
                <table>
                    <thead><tr>
                        <th>{t("billing.invoices.number")}</th>
                        <th className="!text-right">{t("billing.invoices.status-label")}</th>
                        <SortableHeader label={t("billing.invoices.created-at")} sortKey="createdAt" sort={sort} onSort={toggleCreatedSort} align="right" className="!text-right"/>
                        <th className="!text-right">{t("billing.invoices.total")}</th>
                        <th className="!text-right"><span className="sr-only">{t("actions")}</span></th>
                    </tr></thead>
                    <tbody>{visibleInvoices.map((invoice) => {
                        const downloadUrl = invoice.invoicePdfUrl || invoice.hostedInvoiceUrl;
                        const statusPresentation = invoice.status ? statusPresentations[invoice.status] : null;
                        return <tr key={invoice.id}>
                            <td className="whitespace-nowrap">{invoice.number || t("billing.invoices.invoice")}</td>
                            <td className="whitespace-nowrap text-right">{invoice.status && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusPresentation?.className ?? "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"}`}>{statusPresentation?.label ?? invoice.status}</span>}</td>
                            <td className="whitespace-nowrap text-right">{formatInvoiceDate(invoice.createdAt, i18n.language)}</td>
                            <td className="text-right font-medium">{formatInvoiceAmount(invoice.total, invoice.currency, i18n.language)}</td>
                            <td className="text-right">{downloadUrl && <a
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-violet-600 transition hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:bg-slate-900 dark:text-violet-400"
                                href={downloadUrl} target="_blank" rel="noopener noreferrer"
                                aria-label={t("billing.invoices.download")} title={t("billing.invoices.download")}
                            ><FontAwesomeIcon icon={faDownload}/></a>}</td>
                        </tr>;
                    })}</tbody>
                </table>
            </div>
            <TablePagination
                page={safePage} pageSize={pageSize} pageSizeOptions={[10, 25, 50]}
                totalItems={sortedInvoices.length} onPageChange={setPage}
                onPageSizeChange={(value) => { setPageSize(value); setPage(1); }} t={t}
            />
        </>}
    </div>;
}
